import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  Users,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Share2,
  DoorClosed,
} from "lucide-react";
import { api, formatDetail } from "@/lib/api";
import { enablePush, pushSupported } from "@/lib/push";
import PublicNav from "@/components/PublicNav";

function formatSigned(seconds) {
  if (seconds == null) seconds = 0;
  const overdue = seconds < 0;
  const abs = Math.abs(seconds);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${overdue ? "-" : ""}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Small helper: fire an OS-level browser notification when possible
function osNotify(title, body) {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, { body, icon: "/favicon.ico", tag: "okay-room" });
    setTimeout(() => n.close(), 15000);
  } catch { /* ignore */ }
}

export default function QueueStatus() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lookupHandle, setLookupHandle] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [guest, setGuest] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  );
  const [pushOn, setPushOn] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Detect iOS — we skip the push UI there entirely (per user request).
    const ua = navigator.userAgent || "";
    setIsIos(/iPad|iPhone|iPod/i.test(ua));
  }, []);

  const doEnablePush = useCallback(async () => {
    if (!id) return;
    const res = await enablePush(id);
    if (res.ok) {
      setPushOn(true);
      setNotifPermission("granted");
      toast.success("Notifications enabled — you can close the tab now.");
    } else if (res.reason === "denied") {
      setNotifPermission("denied");
      toast.error("Notifications are blocked. Enable them in your browser settings.");
    } else if (res.reason === "unsupported") {
      toast.error("Push isn't supported on this browser.");
    } else {
      toast.error("Couldn't enable notifications. Try again.");
    }
  }, [id]);

  const prevTurnRef = useRef(0);
  const prevTimeupRef = useRef(0);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [{ data: g }, { data: s }] = await Promise.all([
        api.get(`/queue/${id}`),
        api.get(`/stats`),
      ]);
      // fire notifications when counters go up
      const nextTurn = g.turn_notified_count || 0;
      const nextTimeup = g.timeup_notified_count || 0;
      if (nextTurn > prevTurnRef.current) {
        toast.success("It's your turn! Head to the entrance now.");
        osNotify("Okay Room · You're up!", `Queue #${g.queue_number} — head to Fleur by Raja Cafe.`);
      }
      if (nextTimeup > prevTimeupRef.current) {
        toast.warning("Your 30 minutes is up. Please make room for the next guest.");
        osNotify("Okay Room · Time's up", `Please exit so queue #${nextTimeup + g.queue_number} can come in.`);
      }
      prevTurnRef.current = nextTurn;
      prevTimeupRef.current = nextTimeup;

      setGuest(g);
      setStats(s);
      setError(null);
      if (g.remaining_seconds != null) setRemaining(g.remaining_seconds);
    } catch (e) {
      setError(formatDetail(e.response?.data?.detail) || "Queue entry not found");
      setGuest(null);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    // Auto-enable push if already granted — but skip on iOS to keep the old behavior there.
    const ua = navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/i.test(ua);
    if (!iOS && pushSupported() && typeof Notification !== "undefined" && Notification.permission === "granted") {
      enablePush(id).then((res) => setPushOn(!!res.ok));
    }
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // local ticker for countdown (allows negative → overdue)
  useEffect(() => {
    if (guest?.status !== "inside" || remaining == null) return;
    const t = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => clearInterval(t);
  }, [guest?.status, remaining]);

  if (!id) {
    const onLookup = async (e) => {
      e.preventDefault();
      const h = lookupHandle.trim().replace(/^@/, "");
      if (!h) return;
      setLookingUp(true);
      try {
        const { data } = await api.get(`/queue/lookup/${encodeURIComponent(h)}`);
        localStorage.setItem("okay_last_guest", data.id);
        nav(`/queue/${data.id}`);
      } catch (e2) {
        toast.error(formatDetail(e2.response?.data?.detail) || "No queue entry found");
      } finally {
        setLookingUp(false);
      }
    };
    return (
      <div className="min-h-screen bg-background">
        <Toaster position="top-center" richColors />
        <PublicNav />
        <div className="max-w-3xl mx-auto px-6 py-24">
          <p className="label-eyebrow">check queue</p>
          <h1 className="font-display text-5xl mt-4">Find your ticket</h1>
          <p className="text-muted-foreground mt-3">Enter the nickname you registered with.</p>
          <form className="mt-8 flex gap-3" onSubmit={onLookup}>
            <div className="flex-1 flex items-center gap-2 border-b border-border py-2 focus-within:border-foreground transition-colors">
              <input
                value={lookupHandle}
                onChange={(e) => setLookupHandle(e.target.value)}
                placeholder="your nickname"
                data-testid="find-queue-input"
                className="flex-1 bg-transparent focus:outline-none"
                autoFocus
              />
            </div>
            <button
              data-testid="find-queue-submit"
              type="submit"
              disabled={lookingUp}
              className="px-5 py-2 border border-foreground/70 hover:bg-foreground hover:text-background transition-colors text-sm tracking-wider uppercase disabled:opacity-60"
            >
              {lookingUp ? "Looking..." : "Find"}
            </button>
          </form>
          <p className="text-xs text-muted-foreground mt-4">
            Haven't joined yet?{" "}
            <Link to="/" className="link-underline text-foreground" data-testid="lookup-register-link">
              Register here
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  const statusConfig = {
    waiting: { label: "In queue", tone: "text-foreground", icon: Loader2, iconClass: "animate-spin" },
    notified: { label: "It's your turn!", tone: "text-[hsl(var(--primary))]", icon: CheckCircle2 },
    inside: { label: "Currently inside", tone: "text-emerald-700", icon: CheckCircle2 },
    exited: { label: "Session ended", tone: "text-muted-foreground", icon: XCircle },
    cancelled: { label: "Cancelled", tone: "text-muted-foreground", icon: XCircle },
  };
  const sc = guest ? statusConfig[guest.status] || statusConfig.waiting : null;
  const isOverdue = guest?.status === "inside" && remaining !== null && remaining <= 0;

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors />
      <PublicNav />

      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground link-underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to event
        </Link>

        {!isIos && notifPermission === "denied" && (
          <div className="mt-6 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="notif-denied-banner">
            Browser notifications are blocked. Tap the lock icon in your address bar → allow notifications, then reload.
          </div>
        )}
        {!isIos && notifPermission !== "denied" && !pushOn && pushSupported() && (
          <div className="mt-6 border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="enable-push-banner">
            <div>
              <p className="font-display text-lg leading-tight">Enable notifications</p>
              <p className="text-sm text-muted-foreground mt-1">
                We'll ping you even if you close this tab — so you never miss your turn.
              </p>
            </div>
            <button
              onClick={doEnablePush}
              data-testid="enable-push-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-foreground text-background hover:bg-[hsl(var(--primary))] transition-colors text-sm tracking-widest uppercase font-semibold shrink-0"
            >
              Turn on
            </button>
          </div>
        )}
        {!isIos && pushOn && (
          <div className="mt-6 border border-emerald-500/40 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="push-on-banner">
            ✓ Notifications on — you can close this tab. We'll ping you when it's your turn.
          </div>
        )}

        {error && (
          <div className="mt-8 border border-destructive/40 bg-destructive/5 p-6" data-testid="queue-error">
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {guest && (
          <>
            {isOverdue && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 border-2 border-destructive bg-destructive/10 p-6 flex items-start gap-4"
                data-testid="overdue-banner"
              >
                <AlertTriangle className="w-6 h-6 text-destructive shrink-0 mt-0.5" strokeWidth={1.6} />
                <div>
                  <p className="font-display text-2xl text-destructive">Your 30 minutes is up.</p>
                  <p className="text-sm text-destructive/90 mt-1">
                    Please exit so the next guest can come in. We'll re-ping this page every 30 minutes until staff confirms your exit.
                  </p>
                </div>
              </motion.div>
            )}

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Queue number */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="lg:col-span-7 border border-border bg-card p-10 lg:p-14 relative overflow-hidden"
              >
                <div className="label-eyebrow">your queue number</div>
                <div
                  data-testid="queue-number"
                  className={`font-mono-num text-[9rem] lg:text-[13rem] leading-[0.85] tracking-tighter mt-2 ${
                    guest.status === "notified" ? "text-[hsl(var(--primary))]" : "text-foreground"
                  }`}
                >
                  {String(guest.queue_number).padStart(2, "0")}
                </div>
                <div className={`inline-flex items-center gap-2 mt-6 ${sc?.tone}`} data-testid="queue-status">
                  {sc?.icon && <sc.icon className={`w-4 h-4 ${sc.iconClass || ""}`} strokeWidth={1.5} />}
                  <span className="text-sm tracking-widest uppercase font-semibold">{sc?.label}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">Hi {guest.name}, keep this tab open — we ping in real time.</p>
              </motion.div>

              {/* Right side: dynamic panel */}
              <div className="lg:col-span-5 space-y-6">
                {guest.status === "waiting" && stats && (
                  <div className="border border-border p-8 bg-card" data-testid="waiting-panel">
                    <p className="label-eyebrow">position in line</p>
                    <p className="font-mono-num text-5xl mt-2">{guest.position ?? "—"}</p>
                   <div className="mt-6 space-y-2 text-sm">
                     <Row label="Your group" value={guest.people_count || 1} />
                     <Row label="People ahead" value={guest.people_ahead ?? Math.max((guest.position || 1) - 1, 0)} />
                   </div>
                    <p className="text-xs text-muted-foreground mt-6">You'll be pinged here (and via a browser notification if you allowed it) as soon as staff calls you in.</p>
                  </div>
                )}

                {guest.status === "notified" && (
                  <div className="border-2 border-[hsl(var(--primary))] p-8 bg-[hsl(var(--primary))]/5" data-testid="notified-panel">
                    <p className="label-eyebrow text-[hsl(var(--primary))]">head over now</p>
                    <p className="font-display text-3xl mt-3">You've been called.</p>
                    <p className="text-sm text-muted-foreground mt-3">
                      Please come to the entrance and show queue number{" "}
                      <strong className="text-foreground">#{guest.queue_number}</strong> to a staff member.
                    </p>
                  </div>
                )}

                {guest.status === "inside" && (
                  <div
                    className={`border p-8 ${isOverdue ? "border-destructive bg-destructive/5" : "border-emerald-700/30 bg-emerald-50"}`}
                    data-testid="inside-panel"
                  >
                    <p className={`label-eyebrow ${isOverdue ? "text-destructive" : "text-emerald-800"}`}>
                      {isOverdue ? "time exceeded" : "enjoy your stay"}
                    </p>
                    <p className={`label-eyebrow mt-6 ${isOverdue ? "text-destructive" : "text-emerald-800"}`}>
                      {isOverdue ? "over by" : "time remaining"}
                    </p>
                    <div
                      data-testid="countdown-timer"
                      className={`font-mono-num text-6xl lg:text-7xl mt-2 tracking-tighter ${
                        isOverdue ? "text-destructive" : remaining !== null && remaining < 300 ? "text-destructive" : "text-emerald-900"
                      }`}
                    >
                      {formatSigned(remaining ?? 0)}
                    </div>
                    <p className={`text-xs mt-4 ${isOverdue ? "text-destructive/80" : "text-emerald-900/70"}`}>
                      Timer started at{" "}
                      {guest.entered_at ? new Date(guest.entered_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}.
                      {isOverdue ? " Please head out now." : " Please exit when the timer ends."}
                    </p>
                    <button
                      onClick={async () => {
                        if (!window.confirm("Finish your session now? You'll free up space for the next guest.")) return;
                        try {
                          await api.post(`/queue/${id}/self-exit`);
                          toast.success("Session ended — thanks for coming!");
                          load();
                        } catch (e) {
                          toast.error(formatDetail(e.response?.data?.detail) || "Couldn't finish session");
                        }
                      }}
                      data-testid="self-exit-btn"
                      className="mt-6 inline-flex items-center gap-2 px-5 py-3 border border-emerald-800/40 text-emerald-900 hover:bg-emerald-900 hover:text-white transition-colors text-sm tracking-widest uppercase font-semibold"
                    >
                      <DoorClosed className="w-4 h-4" strokeWidth={1.5} /> Finish early
                    </button>
                  </div>
                )}

                {(guest.status === "exited" || guest.status === "cancelled") && (
                  <div className="border border-border p-8 bg-muted" data-testid="ended-panel">
                    <p className="label-eyebrow">session ended</p>
                    <p className="font-display text-2xl mt-3">Thanks for coming!</p>
                    <p className="text-sm text-muted-foreground mt-3">
                      Hope you enjoyed the Okay Room. Share your photos with the tag <strong>#OkayRoom</strong>.
                    </p>
                    <Link
                      to="/"
                      data-testid="new-registration-link"
                      className="inline-flex items-center gap-2 mt-6 text-sm border border-foreground/70 px-4 py-2 hover:bg-foreground hover:text-background transition-colors"
                    >
                      Register again <Users className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* SHARE CARD */}
            {(guest.status === "waiting" || guest.status === "notified" || guest.status === "inside") && (
              <ShareCard guest={guest} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-border pb-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono-num">{value}</span>
    </div>
  );
}

function ShareCard({ guest }) {
  return (
    <div className="mt-12" data-testid="share-card-section">
      <div className="flex items-center justify-between mb-4">
        <p className="label-eyebrow flex items-center gap-2">
          <Share2 className="w-3 h-3" /> your queue card
        </p>
      </div>
      <div className="relative overflow-hidden border border-border bg-gradient-to-br from-[hsl(200_80%_88%)] via-[hsl(200_70%_94%)] to-[hsl(32_65%_92%)] p-8 lg:p-10" data-testid="share-card">
        <div className="absolute inset-0 ripple opacity-70" />
        <div className="relative">
          <p className="label-eyebrow text-[hsl(215_40%_20%)]/80">Okay Room · Aug 1, 2026</p>
          <div className="mt-4 flex items-end gap-4">
            <div className="font-mono-num text-7xl lg:text-8xl leading-none tracking-tighter text-[hsl(215_50%_20%)]">
              #{String(guest.queue_number).padStart(2, "0")}
            </div>
            <div className="pb-2">
              <p className="font-display text-2xl italic text-[hsl(205_70%_38%)]">Room</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[hsl(215_40%_20%)]/80">
            <span>{guest.name}</span>
            <span className="opacity-40">·</span>
            <span>{guest.name}</span>
            <span className="opacity-40">·</span>
            <span>Fleur by Raja Cafe · Melawai, Jakarta</span>
          </div>
        </div>
      </div>
    </div>
  );
}
